import { Component }              from 'react'
import { motion }                  from 'framer-motion'
import { AlertTriangle, RotateCcw } from 'lucide-react'

// ─────────────────────────────────────────────────────────────────────────────
// ErrorFallback — themed UI shown when a boundary catches a render error.
// Uses --ac-* tokens via inline styles so it respects light / dark theme.
// ─────────────────────────────────────────────────────────────────────────────
function ErrorFallback({ error, onReset }) {
  return (
    <div
      role="alert"
      className="min-h-[60vh] flex items-center justify-center p-6"
      style={{ backgroundColor: 'var(--ac-bg-base)' }}
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.34, 1.56, 0.64, 1] }}
        className="w-full max-w-sm flex flex-col items-center text-center"
        style={{
          backgroundColor: 'var(--ac-bg-surface)',
          border:          '1px solid var(--ac-border-base)',
          borderRadius:    'var(--ac-radius-xl)',
          padding:         'var(--ac-space-10)',
          boxShadow:       'var(--ac-shadow-lg)',
          gap:             'var(--ac-space-3)',
        }}
      >
        {/* ── Icon ──────────────────────────────────────────────── */}
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center mb-1 flex-shrink-0"
          style={{ backgroundColor: 'var(--ac-danger-bg)' }}
        >
          <AlertTriangle
            size={28}
            strokeWidth={1.75}
            style={{ color: 'var(--ac-danger)' }}
            aria-hidden="true"
          />
        </div>

        {/* ── Title ─────────────────────────────────────────────── */}
        <p
          className="text-xl font-semibold m-0"
          style={{
            fontFamily: 'var(--ac-font-display)',
            color:      'var(--ac-text-base)',
          }}
        >
          Something went wrong
        </p>

        {/* ── Description ───────────────────────────────────────── */}
        <p
          className="text-sm m-0"
          style={{ color: 'var(--ac-text-muted)', lineHeight: 1.6 }}
        >
          An unexpected error occurred while rendering this section. You can try
          again or reload the entire page.
        </p>

        {/* ── Dev-only error detail ─────────────────────────────── */}
        {import.meta.env.DEV && error?.message && (
          <pre
            className="w-full text-left text-xs overflow-auto m-0"
            style={{
              fontFamily:      'var(--ac-font-mono)',
              color:           'var(--ac-danger-fg)',
              backgroundColor: 'var(--ac-danger-bg)',
              border:          '1px solid var(--ac-danger)',
              borderRadius:    'var(--ac-radius-md)',
              padding:         'var(--ac-space-3) var(--ac-space-4)',
              whiteSpace:      'pre-wrap',
              wordBreak:       'break-word',
              marginTop:       'var(--ac-space-1)',
            }}
          >
            {error.message}
          </pre>
        )}

        {/* ── Actions ───────────────────────────────────────────── */}
        <div
          className="flex flex-wrap justify-center mt-2"
          style={{ gap: 'var(--ac-space-3)' }}
        >
          {/* Primary — try again (resets the boundary in-place) */}
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-2 px-5 h-10 rounded-xl text-sm font-semibold"
            style={{
              background:   'linear-gradient(135deg, var(--ac-secondary) 0%, #6D28D9 100%)',
              color:        'var(--ac-secondary-fg)',
              border:       'none',
              cursor:       'pointer',
              boxShadow:    '0 4px 14px rgba(124,58,237,0.25)',
              transition:   'opacity 150ms ease',
              outline:      '2px solid transparent',
              outlineOffset: '2px',
            }}
            onFocus={e  => { e.currentTarget.style.outline = '2px solid var(--ac-border-focus)' }}
            onBlur={e   => { e.currentTarget.style.outline = '2px solid transparent' }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '0.88' }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1' }}
          >
            <RotateCcw size={14} aria-hidden="true" />
            Try again
          </button>

          {/* Secondary — hard reload */}
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 px-5 h-10 rounded-xl text-sm font-medium"
            style={{
              backgroundColor: 'transparent',
              color:           'var(--ac-text-muted)',
              border:          '1px solid var(--ac-border-base)',
              cursor:          'pointer',
              transition:      'opacity 150ms ease',
              outline:         '2px solid transparent',
              outlineOffset:   '2px',
            }}
            onFocus={e  => { e.currentTarget.style.outline = '2px solid var(--ac-border-focus)' }}
            onBlur={e   => { e.currentTarget.style.outline = '2px solid transparent' }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '0.7' }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1' }}
          >
            Reload page
          </button>
        </div>
      </motion.div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// ErrorBoundary — class component (required by React for getDerivedStateFromError).
//
// Usage:
//   <ErrorBoundary>
//     <FeatureModule />
//   </ErrorBoundary>
//
//   Custom fallback:
//   <ErrorBoundary fallback={({ error, onReset }) => <MyFallback … />}>
//     …
//   </ErrorBoundary>
// ─────────────────────────────────────────────────────────────────────────────
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, info) {
    // TODO: wire up error reporter (Sentry / Datadog)
    console.error('[ErrorBoundary]', error, info)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback({
          error:   this.state.error,
          onReset: this.handleReset,
        })
      }
      return (
        <ErrorFallback
          error={this.state.error}
          onReset={this.handleReset}
        />
      )
    }

    return this.props.children
  }
}
